import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search, X, BookMarked } from "lucide-react";
import { GLOSSARY } from "@/data/glossary";

export default function GlossaryContent() {
  const [query, setQuery] = useState("");
  const location = useLocation();

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return GLOSSARY;
    return GLOSSARY.filter((t) =>
      t.term.toLowerCase().includes(q) ||
      t.def.toLowerCase().includes(q) ||
      (t.aliases || []).some((a) => a.toLowerCase().includes(q))
    );
  }, [query]);

  // Deep-link support: /glossary#mvr scrolls to and highlights that term.
  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(`term-${hash}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-brand");
      setTimeout(() => el.classList.remove("ring-2", "ring-brand"), 2200);
    }
  }, [location.hash]);

  return (
    <div>
      <div className="sticky top-2 z-10">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            data-testid="glossary-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search terms — try 'MVR', 'authority', 'fuel tax'…"
            className="w-full h-14 pl-12 pr-12 rounded-full border border-slate-200 bg-white shadow-sm text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
          />
          {query && (
            <button
              data-testid="glossary-clear"
              onClick={() => setQuery("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label="Clear search"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-500" data-testid="glossary-count">
        {results.length} {results.length === 1 ? "term" : "terms"}{query ? ` matching “${query}”` : ""}
      </p>

      {results.length === 0 ? (
        <div className="mt-10 text-center py-16 rounded-2xl border border-dashed border-slate-300" data-testid="glossary-empty">
          <BookMarked className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No terms match “{query}”.</p>
          <p className="text-sm text-slate-400 mt-1">Try a shorter word or an abbreviation.</p>
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {results.map((t) => (
            <div
              key={t.id}
              id={`term-${t.id}`}
              data-testid={`glossary-term-${t.id}`}
              className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md"
            >
              <h3 className="font-heading text-lg font-black tracking-tight text-slate-900">{t.term}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.def}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
